import { defineAsyncComponent } from 'vue';
import format from '@/common/utilities/format';
import { resolveHistoryStartIndex, sliceTail } from '@/common/components/mvtestprice/mvt-estpricechart-window.js';

const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DEFAULT_LINE_COLOR = '#212121';
/** Figma homeowner value-history line gradient (left → right). */
const LINE_GRADIENT_START = 'rgba(7, 65, 157, 0)';
const LINE_GRADIENT_MID = '#0064FF';
const LINE_GRADIENT_END = '#07419D';
const END_DOT_COLOR = '#0064FF';
const END_DOT_RADIUS = 5;

function getMonth(index) {
    const tmpDate = new Date();
    tmpDate.setMonth(tmpDate.getMonth() + index);
    const month = tmpDate.getMonth() + 1;
    const year = tmpDate.getFullYear();
    return `${months[month - 1]} ${year}`;
}

const mvtChart = defineAsyncComponent({
    loader: () => import(/* webpackChunkName: "mvt-chart" */ '@/common/components/mvtchart/mvt-chart.vue'),
});

export default {
    name: 'mvtEstPriceChart',
    components: {
        mvtChart,
    },
    props: {
        price: {
            type: Number,
            default: 0,
        },
        history: {
            type: Array,
            default: () => [],
        },
        marketTrend: {
            type: Object,
            default: null,
        },
        listPrice: {
            type: Number,
            default: 0,
        },
        houseRealStatus: {
            type: String,
            default: '',
        },
        cdnUrl: {
            type: String,
            default: '',
        },
        address: {
            type: String,
            default: '',
        },
        showDescription: {
            type: Boolean,
            default: true,
        },
        /** Override line color; defaults to DPP black. */
        lineColor: {
            type: String,
            default: DEFAULT_LINE_COLOR,
        },
        yAxisPosition: {
            type: String,
            default: 'right',
        },
        /** When false, hides horizontal Y-axis grid lines (parallel to the year axis). */
        showYGridLines: {
            type: Boolean,
            default: true,
        },
        /** When false, hides the price (Y) axis labels and border. Year (X) axis stays visible. */
        showYAxis: {
            type: Boolean,
            default: true,
        },
        /** When true, paints the main line with the homeowner Figma left→right gradient. */
        useLineGradient: {
            type: Boolean,
            default: false,
        },
        /** When true, shows a filled blue point on the last data value. */
        showEndDot: {
            type: Boolean,
            default: false,
        },
        /** When true, draws the horizontal baseline above year labels (X-axis line only, no vertical grids). */
        showXAxisLine: {
            type: Boolean,
            default: false,
        },
        /** X-axis year label rotation in degrees. DPP uses 25; Figma homeowner uses 0 (horizontal). */
        xAxisLabelRotation: {
            type: Number,
            default: 25,
        },
        /** Extra space between the X-axis line and year labels. */
        xAxisTickPadding: {
            type: Number,
            default: 3,
        },
        /** When true, hides the bottom-most Y tick label to avoid overlapping the first year. */
        hideMinYTickLabel: {
            type: Boolean,
            default: false,
        },
        /**
         * Homeowner only: use a hard Y max (with sold-marker headroom) so the SOLD icon is not clipped.
         * DPP leaves false and keeps suggestedMin/suggestedMax.
         */
        fitSoldMarker: {
            type: Boolean,
            default: false,
        },
        /**
         * When true, the price axis starts at $0 and cuts the empty range below the data
         * with a break mark, so $0 is on the axis without flattening the prices at the top.
         */
        yAxisFromZero: {
            type: Boolean,
            default: false,
        },
        /** DPP default: start the series at the first January in the payload. */
        alignToJanuary: {
            type: Boolean,
            default: true,
        },
        /** When set with alignToJanuary=false, use the last N months instead of January alignment. */
        historyMonths: {
            type: Number,
            default: null,
        },
        /** Slice the built series to the last N months (homeowner range filter). */
        visibleMonths: {
            type: Number,
            default: null,
        },
        /** Show an X-axis label every N points. DPP uses 12 (years). */
        xAxisTickEvery: {
            type: Number,
            default: 12,
        },
    },
    emits: ['forecast'],
    data() {
        return {
            forecastPrice: 0,
            forecastPriceRate: 0,
            forecastLabel: '',
            lastMonthPrice: 0,
            price12MonthsAgo: null,
            rangeStartPrice: null,
            allTimeStartPrice: null,
        };
    },
    watch: {
        forecastPrice: 'emitForecast',
        rangeStartPrice: 'emitForecast',
        visibleMonths: 'emitForecast',
    },
    methods: {
        emitForecast() {
            if (!this.forecastPrice) {
                return;
            }
            this.$emit('forecast', {
                price: this.forecastPrice,
                rate: this.forecastPriceRate,
                label: this.forecastLabel,
                lastMonthPrice: this.lastMonthPrice,
                price12MonthsAgo: this.price12MonthsAgo,
                rangeStartPrice: this.rangeStartPrice,
                allTimeStartPrice: this.allTimeStartPrice,
            });
        },
    },
    computed: {
        priceHistoryData() {
            const datePriceMap = {};
            this.history &&
                this.history
                    .filter((item) => item.status === 'Sold')
                    .forEach((item) => {
                        const date = new Date(item.dateMobile);
                        const monthYear = `${months[date.getMonth()]} ${date.getFullYear()}`;
                        datePriceMap[monthYear] = item.priceDesktop.replace(/[$,]/g, '');
                    });
            return datePriceMap;
        },
        chartData() {
            const data = this.marketTrend;
            if (!data || !data.dailyCityStatisticsList || !data.dailyCityStatisticsList.length) {
                return null;
            }
            const currentIcon = typeof Image !== 'undefined' ? new Image(0, 0) : null;
            const estPrice = this.price;
            if (data && estPrice) {
                const maxItem = data.dailyCityStatisticsList;
                const startIndex = resolveHistoryStartIndex(maxItem, {
                    alignToJanuary: this.alignToJanuary,
                    historyMonths: this.historyMonths,
                });
                const date = new Date();
                const monthYear = `${months[date.getMonth()]} ${date.getFullYear()}`;
                let label = [],
                    dataset = [],
                    label1 = [monthYear],
                    dataset1 = [estPrice];
                for (let i = maxItem.length - 1; i >= startIndex; i--) {
                    const item = maxItem[i].allTypeMedianListPrice;
                    const item1 = maxItem[i - 1] ? maxItem[i - 1].allTypeMedianListPrice : item;
                    //------- trick code not matter what years date we are using, treat them as last 5 years
                    label.unshift(getMonth(i - maxItem.length));
                    //------- tip: the real data should be maxItem[i].monthYear;
                    if (!item1 || !item) {
                        dataset.unshift(dataset[0] || estPrice);
                    } else if (dataset.length) {
                        dataset.unshift(Math.floor((item1 / item) * dataset[0]));
                    } else {
                        dataset.unshift(Math.floor((item1 / item) * estPrice));
                    }
                }

                const lastMonth = label.length && label[label.length - 1];
                let pointStyleForSold = new Array(dataset.length);
                let newSoldDataSet = new Array(dataset.length);
                let moneyIcon = typeof Image !== 'undefined' ? new Image() : null;
                if (moneyIcon) {
                    moneyIcon.src = `${this.cdnUrl}images/desktop/icon-last-sold.svg`;
                }
                for (let i = 0; i < label.length; i++) {
                    const soldPrice = this.priceHistoryData[label[i]];
                    if (soldPrice && soldPrice > 0) {
                        dataset[i] = soldPrice;
                        newSoldDataSet[i] = soldPrice;
                        pointStyleForSold[i] = moneyIcon;
                    }
                }

                const firstPrice = dataset.length && dataset[0];
                const lastPrice = dataset.length && dataset[dataset.length - 1];
                const monthCount = dataset.length;
                this.price12MonthsAgo = dataset.length >= 12 ? dataset[dataset.length - 12] : null;
                this.allTimeStartPrice = firstPrice || null;
                if (this.visibleMonths) {
                    label = sliceTail(label, this.visibleMonths);
                    dataset = sliceTail(dataset, this.visibleMonths);
                    newSoldDataSet = sliceTail(newSoldDataSet, this.visibleMonths);
                    pointStyleForSold = sliceTail(pointStyleForSold, this.visibleMonths);
                }
                this.rangeStartPrice = dataset[0] || null;
                const yearCount = monthCount / 12;
                const yearMinRate = 0.02;
                let yearRate = 0;
                if (firstPrice) {
                    yearRate = (lastPrice - firstPrice) / firstPrice / yearCount;
                }
                //------- trick code if price increase rate can't be samller than 0.02
                if (yearRate < yearMinRate) {
                    yearRate = yearMinRate;
                }
                //------- tip: the real data should be yearRate
                this.forecastPriceRate = Math.floor(yearRate * 100);
                const monthRate = yearRate / 12;
                for (let j = 2; j < maxItem.length; j++) {
                    const tmpDate = new Date(`01 ${lastMonth}`);
                    tmpDate.setMonth(tmpDate.getMonth() + j);
                    const month = tmpDate.getMonth() + 1;
                    const year = tmpDate.getFullYear();
                    if (j <= 12) {
                        label1.push(`${months[month - 1]} ${year}`);
                        dataset1.push(Math.floor(estPrice * (1 + monthRate * j)));
                    }
                }
                if (dataset.length > 1) {
                    this.lastMonthPrice = dataset[dataset.length - 1];
                }
                const emptyCount = dataset.length;
                dataset1 = new Array(emptyCount).concat(dataset1);
                const dataset2 = new Array(emptyCount);
                const pointStyle = new Array(emptyCount);
                dataset2.push(this.listPrice);
                pointStyle.push(currentIcon);
                dataset.push(estPrice);
                label.push(label1[0]);
                dataset1 = dataset1.slice(0, emptyCount + 1);
                const buffer = 0.01;
                // Ignore non-positive listPrice (prop default is 0). Including 0 forces min=$0 and
                // crushes peaks into the canvas top — DPP always passes a real dpp.price.
                const priceRangeValues = [...dataset, this.listPrice].filter((value) => Number(value) > 0);
                const max = Math.max.apply(this, priceRangeValues);
                const min = Math.min.apply(this, priceRangeValues);
                const hasSoldMarker = newSoldDataSet.some((value) => Number(value) > 0);
                const scaleMin = min * (1 - buffer);
                const scaleMax = max * (1 + (this.fitSoldMarker && hasSoldMarker ? 0.3 : buffer * 4));
                // Homeowner (fitSoldMarker): hard min/max so Chart.js cannot clamp sold peaks under a nice tick.
                // DPP: suggestedMin/Max only — unchanged from historical behavior.
                let yTickScale = this.fitSoldMarker ? { min: scaleMin, max: scaleMax } : { suggestedMin: scaleMin, suggestedMax: max * (1 + buffer * 4) };
                if (this.yAxisFromZero) {
                    yTickScale = { min: 0, max: scaleMax };
                }

                const hideMinYTickLabel = this.hideMinYTickLabel;
                const yAxisController = [
                    {
                        display: this.showYAxis,
                        ...(this.yAxisFromZero ? { type: 'zerobreak', breakValue: scaleMin } : {}),
                        ticks: {
                            maxTicksLimit: 6,
                            userCallback: function (value, index) {
                                if (hideMinYTickLabel && index === 0) {
                                    return '';
                                }
                                return '$' + format.friendlyPrice(value);
                            },
                            ...yTickScale,
                        },
                        gridLines: {
                            display: this.showYGridLines,
                            // No vertical price-axis border when grids are off (Figma homeowner).
                            drawBorder: this.showYAxis && this.showYGridLines,
                            color: 'rgba(0, 0, 0, 0.1)',
                        },
                        position: this.yAxisPosition,
                    },
                ];
                this.forecastPrice = dataset1[dataset1.length - 1];
                const annotations = [
                    {
                        backgroundColor: 'rgba(128, 128, 128, 0.1)',
                        drawTime: 'beforeDatasetsDraw',
                        type: 'box',
                        xMax: label1[label1.length - 1],
                        xMin: 'current',
                        xScaleID: 'x-axis-0',
                        yScaleID: 'y-axis-0',
                        mode: 'vertical',
                    },
                ];
                const lineColor = this.lineColor || DEFAULT_LINE_COLOR;
                const lastPointIndex = dataset.length - 1;
                const mainPointRadius = this.showEndDot
                    ? dataset.map((_, index) => (index === lastPointIndex ? END_DOT_RADIUS : 0))
                    : 0;
                const moreDataSets = [];
                this.houseRealStatus === 'ACTIVE' &&
                    moreDataSets.push({
                        label: 'Listed For',
                        data: dataset2,
                        fill: false,
                        borderColor: lineColor,
                        backgroundColor: lineColor,
                        pointStyle: pointStyle,
                        displayColors: false,
                        pointHitRadius: 200,
                        order: 1,
                    });
                const chartConfig = {
                    type: 'line',
                    data: {
                        labels: label,
                        datasets: [
                            {
                                label: '',
                                data: dataset,
                                fill: false,
                                borderColor: this.useLineGradient ? LINE_GRADIENT_MID : lineColor,
                                pointRadius: mainPointRadius,
                                pointHoverRadius: this.showEndDot ? END_DOT_RADIUS : 3.5,
                                pointBackgroundColor: this.showEndDot ? END_DOT_COLOR : lineColor,
                                pointBorderColor: this.showEndDot ? '#ffffff' : lineColor,
                                pointBorderWidth: this.showEndDot ? 2 : 0,
                                backgroundColor: this.useLineGradient ? LINE_GRADIENT_MID : lineColor,
                                borderWidth: 2,
                                displayColors: false,
                                pointHitRadius: 200,
                                order: 2,
                                lineTension: 0,
                            },
                            {
                                label: 'Sold For',
                                data: newSoldDataSet,
                                borderColor: lineColor,
                                pointRadius: 0,
                                backgroundColor: lineColor,
                                borderWidth: 2,
                                pointStyle: pointStyleForSold,
                                displayColors: true,
                                pointHitRadius: 200,
                                order: 0,
                                lineTension: 0,
                            },
                            ...moreDataSets,
                        ],
                    },
                    options: {
                        interaction: {
                            intersect: false,
                            mode: 'nearest',
                        },
                        hover: {
                            animationDuration: 5,
                            mode: 'nearest',
                            intersect: false,
                        },
                        elements: {
                            point: {
                                hoverRadius: 3.5,
                            },
                        },
                        legend: {
                            display: false,
                        },
                        annotation: {
                            annotations: annotations,
                        },
                        // End-dot radius+border sits on the last tick; pad so overflow:hidden on .mvt-chart does not clip it.
                        layout: this.showEndDot
                            ? { padding: { right: END_DOT_RADIUS + 4 } }
                            : undefined,
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                            yAxes: yAxisController,
                            xAxes: [
                                {
                                    display: true,
                                    gridLines: this.showXAxisLine
                                        ? {
                                              // Baseline above years only — no vertical grid lines across the plot.
                                              display: true,
                                              drawOnChartArea: false,
                                              drawTicks: false,
                                              drawBorder: true,
                                              color: 'rgba(0, 0, 0, 0.12)',
                                              zeroLineColor: 'rgba(0, 0, 0, 0.12)',
                                              lineWidth: 1,
                                          }
                                        : {
                                              display: false,
                                              color: 'rgba(0, 0, 0, .1)',
                                          },
                                    ticks: {
                                        autoSkip: false,
                                        maxRotation: this.xAxisLabelRotation,
                                        minRotation: this.xAxisLabelRotation,
                                        padding: this.xAxisTickPadding,
                                        callback: (val, index) => {
                                            const tickEvery = this.xAxisTickEvery || 12;
                                            if (index % tickEvery !== 0 || !val) {
                                                return '';
                                            }
                                            return tickEvery >= 12 ? val.split(' ')[1] : val.split(' ')[0].slice(0, 3);
                                        },
                                    },
                                },
                            ],
                        },
                        tooltips: {
                            enabled: true,
                            displayColors: false,
                            titleAlign: 'center',
                            bodyAlign: 'center',
                            callbacks: {
                                label: (tooltipItems, list) => {
                                    const prefix = '$';
                                    const tooltipPrefix = list.datasets && list.datasets[tooltipItems.datasetIndex] ? list.datasets[tooltipItems.datasetIndex].label + ' ' : '';
                                    if (tooltipItems.datasetIndex == 1) {
                                        delete tooltipItems.yLabel;
                                    }
                                    return tooltipItems.datasetIndex == 0 && this.priceHistoryData[tooltipItems.label]
                                        ? ''
                                        : `${tooltipPrefix}${prefix}${format.formatNumberByComma(tooltipItems.value)}`;
                                },
                            },
                        },
                    },
                    plugins: [
                        {
                            afterLayout: (chart) => {
                                if (!this.useLineGradient) {
                                    return;
                                }
                                const chartArea = chart.chartArea;
                                if (!chartArea) {
                                    return;
                                }
                                const gradient = chart.ctx.createLinearGradient(chartArea.left, 0, chartArea.right, 0);
                                gradient.addColorStop(0, LINE_GRADIENT_START);
                                gradient.addColorStop(0.5, LINE_GRADIENT_MID);
                                gradient.addColorStop(1, LINE_GRADIENT_END);
                                const mainDataset = chart.config.data.datasets[0];
                                if (mainDataset) {
                                    mainDataset.borderColor = gradient;
                                }
                            },
                            afterDraw: (chart) => {
                                if (chart.tooltip?._active?.length) {
                                    const x = chart.tooltip._active[0]._model.x;
                                    const yAxis = chart.scales['y-axis-0'];
                                    const ctx = chart.ctx;
                                    ctx.save();
                                    ctx.beginPath();
                                    ctx.moveTo(x, yAxis.top);
                                    ctx.lineTo(x, yAxis.bottom);
                                    ctx.lineWidth = 1;
                                    ctx.strokeStyle = '#000000';
                                    ctx.stroke();
                                    ctx.restore();
                                }
                            },
                        },
                    ],
                };
                return chartConfig;
            }
            return null;
        },
    },
};
